import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, ApiError } from '../services/api';
import type { User, Video } from '../types/api';
import { cacheManager, CACHE_TTL } from '../services/cacheManager';

export function useUser() {
  const { isAuthenticated, user } = useAuth();
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setUserData(null);
      return;
    }

    // ✅ FIX #2: Check cache first to prevent duplicate API calls
    const cacheKey = `user_${user.id}`;
    const cachedUser = cacheManager.get<User>(cacheKey);
    if (cachedUser) {
      setUserData(cachedUser);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const userData = await apiClient.getUser({ supabase_id: user.id });
      setUserData(userData);
      
      // Cache for 30 seconds
      cacheManager.set(cacheKey, userData, 30000);
    } catch (err) {
      console.error('❌ [useApi] Error fetching user:', err);
      setError(
        err instanceof ApiError ? err.message : 'Failed to fetch user data'
      );
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return {
    user: userData,
    loading,
    error,
    refetch: fetchUser,
  };
}

export function useVideos() {
  const { isAuthenticated } = useAuth();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVideos = useCallback(async () => {
    if (!isAuthenticated) {
      setVideos([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const videoList = await apiClient.getVideos();
      setVideos(videoList);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Failed to fetch videos'
      );
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  return {
    videos,
    loading,
    error,
    refetch: fetchVideos,
  };
}
